<template>
	<AuthShell class="login">
		<Card class="login__card">
			<template #title>Prijava</template>
			<template #content>
				<form @submit.prevent="submit()">
					<Message
						v-if="route.query.reset"
						class="login__message"
						role="status"
						severity="success">
						Geslo je ponastavljeno. Prijavite se z novim geslom.
					</Message>
					<Message
						v-if="submitError"
						class="login__message"
						role="alert"
						severity="error">
						{{ submitError }}
					</Message>
					<FormInput
						label="E-pošta"
						:error-message="formErrors?.email?.[0]">
						<InputText
							v-model="form.email"
							autocomplete="username"
							class="login__input"
							inputmode="email" />
					</FormInput>
					<FormInput
						label="Geslo"
						:error-message="formErrors?.password?.[0]">
						<InputText
							v-model="form.password"
							autocomplete="current-password"
							class="login__input"
							type="password" />
					</FormInput>
					<div class="login__remember-forgot-container">
						<div class="login__remember-container">
							<Checkbox
								v-model="form.remember"
								binary
								input-id="remember" />
							<label
								class="login__remember-label"
								for="remember"
								>Zapomni si me</label
							>
						</div>
						<router-link
							class="login__forgot-password"
							:to="{ name: 'forgot-password' }"
							>Ste pozabili geslo?
						</router-link>
					</div>
					<Button
						class="login__login-button"
						label="Prijava"
						type="submit"
						:loading />
				</form>
			</template>
		</Card>
	</AuthShell>
</template>

<script setup lang="ts">
import AuthShell from '@/components/AuthShell.vue'
import { ref } from 'vue'
import { useAuthStore } from '@/stores/Auth'
import { useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import Checkbox from 'primevue/checkbox'
import Message from 'primevue/message'
import FormInput from '@/components/FormInput.vue'
import { apiErrorMessage } from '@/helpers/api/errorMessage'

const form = ref({
	email: '',
	password: '',
	remember: false,
})
const formErrors = ref<{
	email?: string[]
	password?: string[]
} | null>(null)
const loading = ref(false)
const submitError = ref('')

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

async function submit() {
	// Enter in a field while the request runs must not send it again.
	if (loading.value) return
	formErrors.value = null
	submitError.value = ''
	loading.value = true
	try {
		await auth.login(form.value)
	} catch (error: any) {
		// Wrong credentials and too many attempts arrive as field errors; anything else is shown
		// above the form, with the API's own message when it has one.
		if (error.response?.status === 422 && error.response.data?.errors) {
			formErrors.value = error.response.data.errors
		} else {
			submitError.value = await apiErrorMessage(
				error,
				'Prijava ni uspela. Preverite povezavo in poskusite znova.',
			)
		}
		return
	} finally {
		loading.value = false
	}
	router.push('/')
}
</script>

<style scoped lang="scss">
.login {
	.login__card {
		width: 100%;
		overflow: hidden;

		form {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			gap: 8px;

			& > * {
				width: 100%;
			}

			.login__remember-forgot-container {
				display: flex;
				justify-content: space-between;
				align-items: center;
				width: 100%;
				margin-bottom: 16px;

				.login__remember-container {
					display: flex;
					align-items: center;
					gap: 8px;
					white-space: nowrap;

					.login__remember-label {
						cursor: pointer;
						font-size: 14px;
					}
				}

				.login__forgot-password {
					font-size: 14px;
					color: var(--p-primary-color);
					text-decoration: none;
				}
			}
		}
	}
}
</style>
